import { Helmet } from "react-helmet";
import { useState, useMemo } from "react";
import {
  FaBoxOpen,
  FaMagnifyingGlass,
  FaTag,
  faMoneyBillWave, // Not available in fa6, using alternative
  FaSackDollar,
  FaPercent,
  FaPenToSquare,
  FaTrashCan,
  FaFilter,
  FaPlus,
} from "react-icons/fa6";
import { BtnDefault } from "../../../components/buttons/BtnDefault";
import { Header } from "../../../components/Header";
import { DashboardLayout } from "../../../components/layouts/DashboardLayout";
import { FaCheckCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

/* ============================================
   HELPER: Format Rupiah
   ============================================ */
const formatRupiah = (number) => {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(number);
};

/* ============================================
   DATA DUMMY (Sesuai Skema)
   ============================================ */
// Kita gabungkan packages dan features di sini untuk simulasi
const dummyPackages = [
  {
    id: "pkg-001",
    name: "Home Fiber 10 Mbps",
    price: 150000,
    discount: 0,
    discount_start: null,
    discount_end: null,
    description: "Paket hemat untuk rumahan tangga.",
    features: [
      "Unlimited Quota",
      "Best Effort 10 Mbps",
      "Support Standard"
    ],
  },
  {
    id: "pkg-002",
    name: "Home Fiber 20 Mbps",
    price: 250000,
    discount: 10, // Diskon 10%
    discount_start: "2023-01-01",
    discount_end: "2023-12-31",
    description: "Streaming lancar tanpa buffering.",
    features: [
      "Unlimited Quota",
      "Best Effort 20 Mbps",
      "Support Prioritas",
      "Free Instalasi"
    ],
  },
  {
    id: "pkg-003",
    name: "Business Pro 50 Mbps",
    price: 750000,
    discount: 0,
    discount_start: null,
    discount_end: null,
    description: "Dedicated bandwidth untuk UMKM.",
    features: [
      "1:1 Dedicated 50 Mbps",
      "1 Static IP Public",
      "SLA 99.5%",
      "24/7 Support VIP"
    ],
  },
  {
    id: "pkg-004",
    name: "Corporate 100 Mbps",
    price: 1500000,
    discount: 15,
    discount_start: "2023-10-01",
    discount_end: "2023-11-30",
    description: "Koneksi stabil untuk kantor besar.",
    features: [
      "1:1 Dedicated 100 Mbps",
      "5 Static IP Public",
      "SLA 99.9%",
      "Account Manager",
      "Backup Link (Optional)"
    ],
  },
];

/* ============================================
   KOMPONEN: Skeleton
   ============================================ */
const SkeletonRow = ({ cols = 6 }) => (
  <tr className="animate-pulse">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="px-4 py-4">
        <div
          className="h-3.5 bg-slate-700/60 rounded-md"
          style={{ width: `${40 + Math.random() * 50}%` }}
        />
      </td>
    ))}
  </tr>
);

/* ============================================
   KOMPONEN: Discount Badge
   ============================================ */
const DiscountBadge = ({ discount, start, end }) => {
  if (!discount || discount <= 0) return null;

  // Simulasi cek tanggal (dalam real app gunakan date-fns atau moment)
  const now = new Date();
  const startDate = new Date(start);
  const endDate = new Date(end);
  
  const isActive = start && end ? (now >= startDate && now <= endDate) : true;

  if (!isActive) return null;

  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide bg-pink-500/12 text-pink-400 border border-pink-500/20">
      <FaPercent className="text-[10px]" />
      Diskon {discount}%
    </span>
  );
};

/* ============================================
   KOMPONEN: PackagePage
   ============================================ */
export const PackagePage = () => {
    const navigate = useNavigate();
  const [showSidebar, setShowSidebar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState(dummyPackages);

  /* ---------- Filter & Search ---------- */
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDiscount, setFilterDiscount] = useState(""); // 'active', 'none', ''

  /* ---------- Client-side Filter ---------- */
  const filteredData = useMemo(() => {
    const now = new Date();

    return packages.filter((item) => {
      const matchesSearch =
        item?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.description?.toLowerCase().includes(searchTerm.toLowerCase());

      let matchesDiscount = true;
      if (filterDiscount === "active") {
        // Cek apakah diskon aktif berdasarkan tanggal dummy
        const isActive = item.discount_start && item.discount_end 
          ? (now >= new Date(item.discount_start) && now <= new Date(item.discount_end)) 
          : (item.discount > 0);
        
        matchesDiscount = isActive;
      } else if (filterDiscount === "none") {
        matchesDiscount = item.discount === 0 || !item.discount;
      }

      return matchesSearch && matchesDiscount;
    });
  }, [packages, searchTerm, filterDiscount]);

  /* ---------- Actions ---------- */
  const handleDelete = (id) => {
    if (window.confirm("Hapus paket ini? Pelanggan yang berlangganan paket ini mungkin terdampak.")) {
      setPackages(packages.filter((p) => p.id !== id));
    }
  };

  const handleEdit = (item) => {
    console.log("Edit package:", item);
  };

  const handleCreate = () => {
    console.log("Create package");
  };

  /* ---------- Hitung Harga Final untuk Display ---------- */
  const getDisplayPrice = (item) => {
    const now = new Date();
    const isDiscountActive = item.discount_start && item.discount_end 
      ? (now >= new Date(item.discount_start) && now <= new Date(item.discount_end)) 
      : (item.discount > 0);

    const finalPrice = isDiscountActive 
      ? item.price - (item.price * (item.discount / 100)) 
      : item.price;

    return { original: item.price, final: finalPrice, active: isDiscountActive };
  };

  /* ---------- Column Config ---------- */
  const columns = [
    { key: "no", label: "No", align: "center", width: "60px" },
    { key: "name", label: "Nama Paket", align: "left" },
    { key: "price", label: "Harga", align: "left", width: "160px" },
    { key: "discount", label: "Promo", align: "center", width: "100px" },
    { key: "features", label: "Fitur Utama", align: "left" },
    { key: "action", label: "Aksi", align: "center", width: "100px" },
  ];

  return (
    <>
      <Helmet>
        <title>Package Management - DANELA NET</title>
        <meta name="description" content="Kelola paket langganan internet dan harga." />
      </Helmet>

      <DashboardLayout
        showSidebar={showSidebar}
        setShowSidebar={setShowSidebar}
      >
        {/* ===== Header ===== */}
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <div className="flex items-center gap-3">
            <h2 className="text-base md:text-xl font-bold text-gray-100">
              Manajemen Paket
            </h2>
            <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-500/10 text-purple-400 border border-purple-500/20">
              {filteredData.length} Paket
            </span>
          </div>
          <BtnDefault onClick={handleCreate}>
            <span className="hidden lg:flex items-center gap-2">
              <FaPlus className="text-xs" />
              Buat Paket Baru
            </span>
            <FaPlus className="lg:hidden" />
          </BtnDefault>
        </Header>

        {/* ===== Content ===== */}
        <main className="p-4 lg:p-6 space-y-5">
          {/* ===== Search & Filter ===== */}
          <section
            className="rounded-2xl p-4 sm:p-5"
            style={{
              background: "rgba(15, 23, 42, 0.5)",
              border: "1px solid rgba(51, 65, 85, 0.3)",
              backdropFilter: "blur(12px)",
            }}
          >
            <div className="flex flex-col lg:flex-row lg:items-end gap-4">
              {/* Search */}
              <div className="w-full lg:max-w-xs flex-shrink-0">
                <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
                  Cari Paket
                </label>
                <div className="relative">
                  <FaMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm" />
                  <input
                    type="text"
                    placeholder="Nama paket atau deskripsi..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                  />
                </div>
              </div>

              {/* Filter Promo */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1.5">
                  <FaFilter className="text-xs text-slate-500" />
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Filter Promo
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                   <select
                    value={filterDiscount}
                    onChange={(e) => setFilterDiscount(e.target.value)}
                    className="col-span-1 lg:col-span-2 w-full px-3 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all appearance-none cursor-pointer"
                  >
                    <option value="" className="bg-slate-800">Semua Status</option>
                    <option value="active" className="bg-slate-800">Sedang Promo</option>
                    <option value="none" className="bg-slate-800">Harga Normal</option>
                  </select>
                </div>
              </div>
            </div>
          </section>

          {/* ===== Desktop Table ===== */}
          <section
            className="hidden lg:block rounded-2xl overflow-hidden"
            style={{
              background: "rgba(15, 23, 42, 0.5)",
              border: "1px solid rgba(51, 65, 85, 0.3)",
              backdropFilter: "blur(12px)",
            }}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr
                    className="border-b"
                    style={{
                      background: "rgba(15, 23, 42, 0.7)",
                      borderColor: "rgba(51, 65, 85, 0.3)",
                    }}
                  >
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        className={`px-4 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400 ${
                          col.align === "center" ? "text-center" : "text-left"
                        }`}
                        style={col.width ? { width: col.width } : undefined}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody
                  className="divide-y"
                  style={{ borderColor: "rgba(51, 65, 85, 0.15)" }}
                >
                  {loading && (
                    <>
                      {[...Array(5)].map((_, i) => (
                        <SkeletonRow key={i} cols={columns.length} />
                      ))}
                    </>
                  )}

                  {!loading && filteredData?.length === 0 && (
                    <tr>
                      <td colSpan={columns.length} className="px-4 py-16 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div
                            className="w-16 h-16 rounded-2xl flex items-center justify-center"
                            style={{
                              background: "rgba(51, 65, 85, 0.2)",
                              border: "1px solid rgba(51, 65, 85, 0.2)",
                            }}
                          >
                            <FaBoxOpen className="text-2xl text-slate-600" />
                          </div>
                          <p className="text-slate-400 font-medium">
                            Paket tidak ditemukan
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    filteredData?.map((item, index) => {
                      const priceInfo = getDisplayPrice(item);
                      return (
                        <tr
                          key={item.id}
                          className="transition-colors duration-150 group"
                          style={{ background: "transparent" }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background =
                              "rgba(168, 85, 247, 0.04)"; // Purple tint hover
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "transparent";
                          }}
                          onClick={() => navigate("/dashboard/package/detail/id")} className="cursor-pointer transition-colors duration-150 hover:bg-cyan-500/[0.03]"
                        >
                          {/* No */}
                          <td className="px-4 py-3 text-center text-slate-500 font-mono text-xs">
                            {index + 1}
                          </td>

                          {/* Name & Desc */}
                          <td className="px-4 py-3">
                            <div>
                              <div className="font-semibold text-gray-100 text-sm mb-0.5 flex items-center gap-2">
                                {item.name}
                                <DiscountBadge 
                                  discount={item.discount} 
                                  start={item.discount_start} 
                                  end={item.discount_end} 
                                />
                              </div>
                              <p className="text-xs text-slate-400 line-clamp-1">
                                {item.description}
                              </p>
                            </div>
                          </td>

                          {/* Price */}
                          <td className="px-4 py-3">
                            {priceInfo.active ? (
                              <div className="flex flex-col">
                                <span className="text-xs text-slate-500 line-through">
                                  {formatRupiah(priceInfo.original)}
                                </span>
                                <span className="text-sm font-bold text-emerald-400">
                                  {formatRupiah(priceInfo.final)}
                                </span>
                              </div>
                            ) : (
                              <span className="text-sm font-bold text-cyan-400">
                                {formatRupiah(item.price)}
                              </span>
                            )}
                          </td>

                          {/* Discount Info Center */}
                          <td className="px-4 py-3 text-center">
                             {priceInfo.active ? (
                                <span className="text-xs font-medium text-pink-400">
                                  {item.discount}%
                                </span>
                             ) : (
                                <span className="text-xs text-slate-600">-</span>
                             )}
                          </td>

                          {/* Features */}
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1.5">
                              {item.features.slice(0, 2).map((feat, idx) => (
                                <span
                                  key={idx}
                                  className="px-2 py-0.5 rounded text-[10px] font-medium bg-slate-700/50 text-slate-300 border border-slate-600/30"
                                >
                                  {feat}
                                </span>
                              ))}
                              {item.features.length > 2 && (
                                <span className="px-2 py-0.5 rounded text-[10px] text-slate-500">
                                  +{item.features.length - 2} lainnya
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Action */}
                          <td className="px-4 py-3">
                            <div className="flex justify-center gap-1.5">
                              <button
                                onClick={() => handleEdit(item)}
                                className="p-2 rounded-lg text-white bg-yellow-500/30 hover:bg-yellow-500/70 transition-all"
                                title="Edit paket"
                              >
                                <FaPenToSquare className="text-sm" />
                              </button>
                              <button
                                onClick={() => handleDelete(item.id)}
                                className="p-2 rounded-lg text-white bg-red-500/30 hover:bg-red-500/70 transition-all"
                                title="Hapus paket"
                              >
                                <FaTrashCan className="text-sm" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </section>

          {/* ===== Mobile Card List ===== */}
          <section className="lg:hidden space-y-3">
             {!loading && filteredData?.length === 0 && (
              <div className="rounded-2xl p-12 text-center bg-slate-800/30 border border-slate-700/30">
                 <p className="text-slate-400">Paket tidak ditemukan</p>
              </div>
             )}

             {!loading && filteredData?.map((item, index) => {
                const priceInfo = getDisplayPrice(item);
                return (
                  <article
                    key={item.id}
                    className="rounded-2xl overflow-hidden"
                    style={{
                      background: "rgba(15, 23, 42, 0.5)",
                      border: "1px solid rgba(51, 65, 85, 0.3)",
                    }}
                  >
                    {/* Header */}
                    <div className="px-4 py-3 bg-slate-800/40 border-b border-slate-700/30 flex justify-between items-center">
                       <span className="text-xs font-mono text-slate-500">#{index + 1}</span>
                       <DiscountBadge 
                          discount={item.discount} 
                          start={item.discount_start} 
                          end={item.discount_end} 
                        />
                    </div>

                    {/* Body */}
                    <div className="p-4">
                        <div className="mb-4">
                            <h3 className="font-bold text-white text-base mb-1">{item.name}</h3>
                            <p className="text-xs text-slate-400">{item.description}</p>
                        </div>

                        {/* Price Block Mobile */}
                        <div className="bg-slate-800/50 rounded-lg p-3 mb-4 border border-slate-700/30 flex justify-between items-center">
                            <div className="flex flex-col">
                                <span className="text-[10px] text-slate-400 uppercase tracking-wide">Harga</span>
                                {priceInfo.active && (
                                    <span className="text-xs text-slate-500 line-through">{formatRupiah(priceInfo.original)}</span>
                                )}
                                <span className={`text-lg font-bold ${priceInfo.active ? 'text-emerald-400' : 'text-cyan-400'}`}>
                                    {formatRupiah(priceInfo.final)}
                                </span>
                            </div>
                            <div className="h-8 w-8 rounded-full bg-slate-700/50 flex items-center justify-center">
                                <FaSackDollar className="text-slate-400" />
                            </div>
                        </div>

                        {/* Features Mini */}
                        <div className="mb-4">
                             <span className="text-[10px] text-slate-500 uppercase font-semibold block mb-2">Fitur Utama</span>
                             <ul className="space-y-1">
                                {item.features.slice(0, 3).map((feat, idx) => (
                                    <li key={idx} className="flex items-center gap-2 text-xs text-slate-300">
                                        <FaCheckCircle className="text-[10px] text-cyan-500/70" />
                                        {feat}
                                    </li>
                                ))}
                             </ul>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 pt-3 border-t border-slate-700/30">
                             <button onClick={() => handleEdit(item)} className="flex-1 py-2 rounded-lg bg-slate-700/50 hover:bg-slate-700 text-slate-300 text-xs font-medium transition-colors">
                                Edit
                             </button>
                             <button onClick={() => handleDelete(item.id)} className="flex-1 py-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs font-medium transition-colors border border-red-500/20">
                                Hapus
                             </button>
                        </div>
                    </div>
                  </article>
                )
             })}
          </section>
        </main>
      </DashboardLayout>
    </>
  );
};